// The Leaflets page's Map tab: every zone at once, coloured by who has it now,
// so nobody gets sent down streets someone else is already doing. Zones
// nobody has are grey: dashed when free, shaded when delivered in the last
// 8 weeks. A red outline means two people are working overlapping streets.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Pill } from '../lib/ui';
import { useLeafletMap, fitPoints } from '../lib/leafletMap';
import { formatDay } from '../lib/trade';
import {
  zoneState, clashes, overlappingPairs, jobProgress, areaKm2, formatCount, roundsToCheck,
  FREE_COLOUR, CLASH_COLOUR, COLOURS, COVERED_DAYS,
} from '../lib/leaflets';
import { byId, PersonTag, JobProgress, ZonesLayer, Facts } from './LeafletsCommon';

const shortDay = (d) => formatDay(d).replace(/ \d{4}$/, '');
const firstName = (p) => (p?.name || '').split(' ')[0];

function ZonePanel({ zone, state, people, data, actions, onClose }) {
  const holders = state.active;
  const streetsDone = (zone.streetsDone || []).filter(s => zone.streets.includes(s.name)).length;
  const lastJob = state.last && data.jobs.find(j => j.zone === zone._id && j.runs.some(r => r.status === 'approved' && String(r.date).slice(0, 10) === state.last));
  return (
    <div className="ui-card lf-panel">
      <div className="lf-panel__head">
        <div>
          <h3>{zone.name}</h3>
          {zone.area && <div className="lf-muted">{zone.area}</div>}
        </div>
        <button type="button" className="lf-x" onClick={onClose} aria-label="Close">&times;</button>
      </div>
      <div className="lf-row">
        {state.state === 'assigned' && <Pill tone="blue">With {holders.map(j => firstName(people[j.distributor])).join(' & ')}</Pill>}
        {state.state === 'covered' && <Pill tone="ok">Done {shortDay(state.last)}</Pill>}
        {state.state === 'free' && <Pill>Free</Pill>}
        {zone.archived && <Pill tone="warn">Archived</Pill>}
      </div>
      <Facts items={[
        ['Homes', zone.homes ? `about ${formatCount(zone.homes)}` : 'not counted'],
        ['Streets', zone.streets.length ? `${streetsDone} of ${zone.streets.length} done` : 'no list'],
        ['Size', `${areaKm2(zone.polygon).toFixed(2)} km²`],
        state.last && ['Last delivered', `${formatDay(state.last)}${lastJob ? `, ${firstName(people[lastJob.distributor])}` : ''}`],
      ]} />
      {holders.map(job => (
        <div key={job._id} className="lf-panel__job">
          <div className="lf-row">
            <PersonTag person={people[job.distributor]} />
            <span className="lf-muted">{formatCount(job.leaflets)} leaflets</span>
            <Button size="sm" style={{ marginLeft: 'auto' }} onClick={() => actions.openJob(job._id)}>Open</Button>
          </div>
          <JobProgress job={job} />
        </div>
      ))}
      <div className="lf-row lf-panel__actions">
        <Button variant="primary" size="sm" onClick={() => actions.assign({ zone: zone._id })}>Give out leaflets</Button>
        <Button size="sm" onClick={() => actions.print({ zone, job: holders[0] || null })}>Print sheet</Button>
        <Button size="sm" variant="muted" onClick={() => actions.editZone(zone)}>Edit zone</Button>
      </div>
    </div>
  );
}

function Overview({ data, people, states, clashList, overlapList, actions, today }) {
  const active = data.jobs.filter(j => j.status === 'active');
  const holding = data.distributors
    .filter(p => active.some(j => j.distributor === p._id))
    .map(p => {
      const jobs = active.filter(j => j.distributor === p._id);
      const left = jobs.reduce((n, j) => n + jobProgress(j).left, 0);
      const zoneNames = jobs.map(j => data.zones.find(z => z._id === j.zone)?.name).filter(Boolean);
      return { person: p, left, zoneNames };
    });
  const counts = Object.values(states).reduce((acc, s) => ({ ...acc, [s.state]: (acc[s.state] || 0) + 1 }), {});
  const month = today.slice(0, 7);
  const deliveredThisMonth = data.jobs.reduce((n, j) => n + j.runs
    .filter(r => r.status === 'approved' && String(r.date).slice(0, 7) === month)
    .reduce((m, r) => m + r.delivered, 0), 0);
  const toCheck = roundsToCheck(data.jobs).length;
  const out = holding.reduce((n, h) => n + h.left, 0);

  return (
    <>
      {clashList.length > 0 && (
        <div className="ui-banner ui-banner--bad lf-clashes">
          <strong>Two people on the same streets</strong>
          <ul>
            {clashList.map(({ a, b }) => (
              <li key={`${a._id}-${b._id}`}>
                {a === b ? `${a.name} is with more than one person.` : `${a.name} and ${b.name} overlap, and both are out with different people.`}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="ui-card lf-panel">
        <h3>Who's where</h3>
        {holding.length ? (
          <ul className="lf-legend">
            {holding.map(({ person, left, zoneNames }) => (
              <li key={person._id} className="is-link" onClick={() => actions.openPerson(person._id)}>
                <PersonTag person={person} />
                <small>{zoneNames.join(', ')} &middot; {formatCount(left)} left</small>
              </li>
            ))}
          </ul>
        ) : <p className="lf-muted">Nobody has leaflets out right now.</p>}
        <ul className="lf-legend lf-legend--key">
          <li><i className="lf-swatch is-free" />Free zone<small>{counts.free || 0}</small></li>
          <li><i className="lf-swatch is-covered" />Delivered in the last {COVERED_DAYS / 7} weeks<small>{counts.covered || 0}</small></li>
          <li><i className="lf-swatch is-clash" />Clash: two people on the same streets</li>
        </ul>
      </div>
      <div className="ui-card lf-panel">
        <h3>Right now</h3>
        <Facts items={[
          ['Leaflets out', formatCount(out)],
          ['Rounds to check', toCheck ? <button type="button" className="lf-link" onClick={actions.openReview}>{toCheck}</button> : '0'],
          ['Delivered this month', formatCount(deliveredThisMonth)],
        ]} />
      </div>
      {overlapList.length > 0 && (
        <div className="ui-card lf-panel">
          <h3>Overlapping zones</h3>
          <p className="lf-muted">Fine as long as they aren't out with two people at once.</p>
          <ul className="lf-plain">
            {overlapList.map(({ a, b }) => <li key={`${a._id}-${b._id}`}>{a.name} and {b.name}</li>)}
          </ul>
        </div>
      )}
    </>
  );
}

export default function LeafletsMapTab({ data, today, actions }) {
  const mapRef = useRef(null);
  const { map, error } = useLeafletMap(mapRef);
  const [selectedId, setSelectedId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const people = useMemo(() => byId(data.distributors), [data.distributors]);

  const zones = useMemo(() => data.zones.filter(z => showArchived || !z.archived), [data.zones, showArchived]);
  const live = useMemo(() => zones.filter(z => !z.archived), [zones]);
  const states = useMemo(() => Object.fromEntries(zones.map(z => [z._id, zoneState(z, data.jobs, today)])), [zones, data.jobs, today]);
  const clashList = useMemo(() => clashes(live, data.jobs), [live, data.jobs]);
  const clashIds = useMemo(() => new Set(clashList.flatMap(c => [c.a._id, c.b._id])), [clashList]);
  const overlapList = useMemo(
    () => overlappingPairs(live).filter(p => !clashList.some(c => c.a === p.a && c.b === p.b)),
    [live, clashList],
  );

  const items = useMemo(() => zones.map((zone) => {
    const st = states[zone._id];
    const clash = clashIds.has(zone._id) ? { stroke: CLASH_COLOUR, dashed: true } : {};
    if (st.state === 'assigned') {
      const holders = [...new Set(st.active.map(j => j.distributor))].map(id => people[id]).filter(Boolean);
      return {
        zone, colour: holders[0]?.colour || COLOURS[0], fill: 0.3,
        label: `${zone.name} · ${holders.map(firstName).join(' & ')}`, ...clash,
      };
    }
    if (st.state === 'covered') {
      return { zone, colour: FREE_COLOUR, fill: 0.22, label: `${zone.name} · done ${shortDay(st.last)}`, ...clash };
    }
    return { zone, colour: FREE_COLOUR, fill: 0.04, dashed: true, label: zone.name, faded: zone.archived, ...clash };
  }), [zones, states, clashIds, people]);

  // Show all the zones the first time there are some
  const framed = useRef(false);
  useEffect(() => {
    if (!map || framed.current || !live.length) return;
    framed.current = true;
    fitPoints(map, live.flatMap(z => z.polygon), 15);
  }, [map, live]);

  useEffect(() => {
    if (!map) return undefined;
    const clear = () => setSelectedId(null);
    map.on('click', clear);
    return () => map.off('click', clear);
  }, [map]);

  const selected = zones.find(z => z._id === selectedId);

  return (
    <div className="lf-map-layout">
      <div>
        <div className="lf-toolbar">
          <span className="lf-muted">Click a zone for its details.</span>
          <Checkbox checked={showArchived} onChange={e => setShowArchived(e.target.checked)} label="Show archived zones" style={{ marginLeft: 'auto', fontSize: 13 }} />
        </div>
        <div className="lf-map-wrap">
          <div ref={mapRef} className="lf-map" />
          {!map && <div className="lf-map-msg">{error || 'Loading the map…'}</div>}
          {map && !data.zones.length && (
            <div className="lf-map-empty">
              <strong>No zones yet</strong>
              <span>Draw the first area you'd like leafleted.</span>
              <Button variant="primary" size="sm" onClick={() => actions.editZone(null)}>+ New zone</Button>
            </div>
          )}
        </div>
        <ZonesLayer map={map} items={items} selectedId={selectedId} onSelect={setSelectedId} />
      </div>
      <div className="lf-side">
        {selected
          ? <ZonePanel zone={selected} state={states[selected._id]} people={people} data={data} actions={actions} onClose={() => setSelectedId(null)} />
          : <Overview data={data} people={people} states={states} clashList={clashList} overlapList={overlapList} actions={actions} today={today} />}
      </div>
    </div>
  );
}
